import { WhiteboardShapeStateType } from ".";
import { IWhiteboardRendererContext, IWhiteboardRenderOptions } from "../whiteboard-renderer";
import { IShapeState, WhiteboardShape } from "./shape";
import { ISize, ISizeableState } from "./util";
import { Image as KonvaImage } from "konva/lib/shapes/Image";
import { equals } from "angular";

export interface IOriginalImageProps {
  name?: string;
  size?: number;
  type?: string;
  width: number;
  height: number;
}

export interface IOriginalImage {
  rawImage: IOriginalImageProps;
}

export interface IImageState extends IShapeState, ISizeableState, IOriginalImage {
  type: "image";
  url: string;
  clientId?: string;
}

export class Image extends WhiteboardShape<IImageState> {
  static stateTypes: WhiteboardShapeStateType[] = ["image"];

  constructor(state: IImageState, context: IWhiteboardRendererContext) {
    super(state, context);

    this.on("transform", (e) => this.transform(e));

    this.image(state);
    this.resize(state.size);
    this.rotation(state.rotationAngle); // rotate image with the right angle on initial loading
  }

  updateState(state: IImageState, options?: IWhiteboardRenderOptions): boolean {
    let updated = false;
    if (!equals(this.state.position, state.position)) {
      this.position(state.position);
      updated = true;
    }
    if (!equals(this.state.size, state.size)) {
      if (options.shapeId === state.id) {
        this.recreate(state);
      } else {
        this.resize(this.state.size);
      }
      updated = true;
    } else if (this.state.zIndex !== state.zIndex) {
      this.state.zIndex = state.zIndex;
    }

    return updated;
  }

  private image(state: IImageState): void {
    this.context.generateFileUrl(state.url).then((url) => {
      KonvaImage.fromURL(url, (node) => {
        node.setAttrs({
          width: state.size.width,
          height: state.size.height,
        });

        this.add(node);
        this.redraw();
      });
    });
  }

  private transform(e) {
    const rotationAngle = e.target.rotation();

    const size: ISize = {
      width: this.scaleX() * this.state.size.width,
      height: Math.abs(this.scaleY()) * this.state.size.height,
    };

    // transform is being invoked anytime the selection triangle changes, we want to rotate the image
    // only if there's difference between current and new angle
    if (rotationAngle !== this.state.rotationAngle) {
      this.state.rotationAngle = rotationAngle;
      this.rotation(rotationAngle);
    }

    this.resize(size);
  }

  private redraw() {
    if (this.getLayer()) {
      this.getLayer().batchDraw();
    }
  }

  private resize(size: ISize): void {
    this.size(size);
  }

  private recreate(state: IImageState) {
    this.removeChildren();
    this.state = state;
    this.scale({ x: 1, y: 1 });
    this.image(state);
    this.resize(state.size);
    this.rotation(state.rotationAngle);
    this.redraw();
  }
}

Image.prototype.selectionOptions = { minSize: 70, resizable: true, rotatable: true };
